import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const ProposalSignup = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    const validateProposalAccess = async () => {
      if (!token) {
        console.error("❌ No token provided");
        toast.error("Invalid proposal link");
        navigate("/");
        return;
      }

      try {
        console.log("🔍 Validating proposal access token:", token);
        console.log("🔍 Full window location:", window.location.href);
        
        // Find the proposal notification by access token
        const { data: proposal, error } = await supabase
          .from('proposal_notifications')
          .select('*')
          .eq('access_token', token)
          .eq('status', 'pending')
          .maybeSingle();

        if (error) throw error;

        if (!proposal) {
          toast.error("Invalid or expired proposal link");
          navigate("/");
          return;
        }

        // Get the contact information separately
        const { data: contact, error: contactError } = await supabase
          .from('profiles')
          .select(`
            id,
            email,
            first_name,
            last_name,
            phone,
            job_title,
            organization_id,
            organization:organizations!organization_id (
              id,
              name
            )
          `)
          .eq('id', proposal.contact_id)
          .single();

        console.log("🔍 Proposal query result:", { proposal, error });
        console.log("🔍 Contact query result:", { contact, contactError });

        if (contactError) throw contactError;
        if (!contact) {
          toast.error("Contact information not found");
          navigate("/");
          return;
        }

        // Check if user is already logged in
        const { data: { user } } = await supabase.auth.getUser();
        
        if (user) {
          // If user is already logged in, redirect to proposal
          navigate(`/proposal/${token}`);
          return;
        }

        // Determine role based on job title
        let invitedRole = 'standard';
        if (contact.job_title) {
          const adminTitles = ['admin', 'administrator', 'director', 'ceo', 'managing director', 'manager'];
          const isAdmin = adminTitles.some(title => 
            contact.job_title?.toLowerCase().includes(title)
          );
          if (isAdmin) {
            invitedRole = 'admin';
          }
        }

        // Store proposal invitation details for signup form
        sessionStorage.setItem('pendingInvitation', JSON.stringify({
          token: proposal.id, // Use proposal notification ID
          contactId: contact.id,
          organizationId: contact.organization_id,
          invitedRole,
          firstName: contact.first_name,
          lastName: contact.last_name,
          phone: contact.phone,
          jobTitle: contact.job_title,
          proposalToken: token, // Store the original access token
          organizationName: contact.organization?.name
        }));

        // Redirect to signup page with email pre-filled
        navigate("/?signup=true&email=" + encodeURIComponent(contact.email || ''));
        toast.success("Please create an account to view your proposal");

      } catch (error) {
        console.error("Error validating proposal access:", error);
        toast.error("Failed to validate proposal access");
        navigate("/");
      } finally {
        setIsValidating(false);
      }
    };

    validateProposalAccess();
  }, [navigate, token]);

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-brand-50 to-brand-100">
        <div className="text-center">
          <p className="text-lg text-gray-600 mb-2">Validating proposal access...</p>
          <p className="text-sm text-gray-500">Token: {token?.substring(0, 20)}...</p>
        </div>
      </div>
    );
  }

  return null;
};

export default ProposalSignup;